export type TeamOfOneReleaseMode = 'awdocs' | 'full' | 'AW_MISSIONS'

export interface ImportMetaEnvLike {
  VITE_TEAMOFONE_RELEASE_MODE?: string
  VITE_TEAMOFONE_AUTH_MODE?: string
  VITE_TEAMOFONE_AWDOCS_PR_REVIEW?: string
  VITE_TEAMOFONE_AWDOCS_AUDIO?: string
}

export interface LaunchConfig {
  releaseMode: TeamOfOneReleaseMode
  defaultAwDocsPath: string
  prReviewEnabledInAwDocsLaunch: boolean
  commentsEnabledInAwDocsLaunch: boolean
  audioEnabledInAwDocsLaunch: boolean
}

export const DEFAULT_AW_DOCS_PATH = '/too/docs/GoHighLevel/ghl-aw-docs/README.md'
export const DEFAULT_TOOL_SELECTOR_PATH = '/tools'
export const DEFAULT_AW_MISSIONS_PATH = '/app/missions'
export const DEFAULT_AW_MISSIONS_TEAM_SLUG = 'teamofone'
export type AwMissionsSection = 'team' | 'missions' | 'radar' | 'metrics'

function readBoolean(value: string | undefined): boolean {
  return value === 'true' || value === '1'
}

function readReleaseMode(env: ImportMetaEnvLike): TeamOfOneReleaseMode {
  if (env.VITE_TEAMOFONE_RELEASE_MODE === 'awdocs') return 'awdocs'
  if (env.VITE_TEAMOFONE_RELEASE_MODE === 'full') return 'full'
  if (
    env.VITE_TEAMOFONE_RELEASE_MODE === 'AW_MISSIONS' ||
    env.VITE_TEAMOFONE_RELEASE_MODE === 'aw_missions'
  ) {
    return 'AW_MISSIONS'
  }
  if (env.VITE_TEAMOFONE_AUTH_MODE === 'dev') return 'full'
  return 'awdocs'
}

export function readLaunchConfig(env?: ImportMetaEnvLike): LaunchConfig {
  const source = env ?? (import.meta.env as unknown as ImportMetaEnvLike)
  const releaseMode = readReleaseMode(source)
  return {
    releaseMode,
    defaultAwDocsPath: DEFAULT_AW_DOCS_PATH,
    prReviewEnabledInAwDocsLaunch: readBoolean(source.VITE_TEAMOFONE_AWDOCS_PR_REVIEW),
    commentsEnabledInAwDocsLaunch: releaseMode === 'awdocs',
    audioEnabledInAwDocsLaunch: readBoolean(source.VITE_TEAMOFONE_AWDOCS_AUDIO),
  }
}

export function isAwDocsLaunch(config: LaunchConfig = readLaunchConfig()): boolean {
  return config.releaseMode === 'awdocs'
}

export function isAwMissionsLaunch(config: LaunchConfig = readLaunchConfig()): boolean {
  return config.releaseMode === 'AW_MISSIONS'
}

export function isFullLaunch(config: LaunchConfig = readLaunchConfig()): boolean {
  return config.releaseMode === 'full'
}

function pathWithoutQuery(path: string): string {
  return path.split(/[?#]/, 1)[0] || '/'
}

function isInternalPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//')
}

function normalizeTeamSlug(teamSlug: string): string {
  const trimmed = teamSlug.trim().replace(/^\/+|\/+$/g, '')
  return trimmed || DEFAULT_AW_MISSIONS_TEAM_SLUG
}

export function teamNameToAwMissionsSlug(teamName: string): string {
  const slug = teamName
    .trim()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || DEFAULT_AW_MISSIONS_TEAM_SLUG
}

export function teamScopedAwMissionsPath(
  section: AwMissionsSection,
  teamSlug: string,
): string {
  return `/too/${encodeURIComponent(normalizeTeamSlug(teamSlug))}/${section}`
}

export function canonicalizeAwMissionsPath(
  raw: string,
  fallbackTeamSlug: string,
): string | null {
  if (!isInternalPath(raw)) return null

  let parsed: URL
  try {
    parsed = new URL(raw, 'https://teamofone.local')
  } catch {
    return null
  }

  const cleanPath = parsed.pathname
  let section: AwMissionsSection | null = null
  let sectionTail = ''

  const appSection = cleanPath.match(/^\/app\/(missions|radar|metrics)(\/.*)?$/)
  if (appSection) {
    section = appSection[1] as AwMissionsSection
    sectionTail = section === 'missions' ? appSection[2] ?? '' : ''
  } else if (cleanPath === '/too/radar' || cleanPath === '/too/metrics') {
    section = cleanPath.endsWith('/metrics') ? 'metrics' : 'radar'
  } else if (
    cleanPath === '/too/team' ||
    cleanPath === '/too/teams' ||
    cleanPath === '/too/org'
  ) {
    section = 'team'
  }

  if (!section) return null

  const query = new URLSearchParams(parsed.search)
  const teamSlug = query.get('team') || fallbackTeamSlug
  query.delete('team')
  const suffix = query.toString()
  const querySuffix = suffix ? `?${suffix}` : ''
  return `${teamScopedAwMissionsPath(section, teamSlug)}${sectionTail}${querySuffix}${parsed.hash}`
}

function isAwDocsPath(path: string, config: LaunchConfig): boolean {
  const cleanPath = pathWithoutQuery(path)
  if (
    cleanPath === '/login' ||
    cleanPath === DEFAULT_TOOL_SELECTOR_PATH ||
    cleanPath === '/too/collab' ||
    cleanPath === '/too/coming-soon'
  ) {
    return true
  }
  if (cleanPath.startsWith('/too/docs/')) return true
  if (/^\/too\/[^/]+\/docs(?:\/.*)?$/.test(cleanPath)) return true
  if (
    config.prReviewEnabledInAwDocsLaunch &&
    /^\/too\/prs\/[^/]+\/[^/]+\/\d+$/.test(cleanPath)
  ) {
    return true
  }
  return false
}

function isAwMissionsPath(path: string, config: LaunchConfig): boolean {
  const cleanPath = pathWithoutQuery(path)
  if (cleanPath === '/login' || cleanPath === '/too/coming-soon') return true
  if (cleanPath === DEFAULT_TOOL_SELECTOR_PATH) return true
  // AW Docs surfaces (e.g. /too/docs/*, /too/<team>/docs/*, /too/collab) are
  // composed in via isAwDocsPath so the AW Docs reader keeps working when
  // launched from AW Missions mode.
  if (isAwDocsPath(path, config)) return true
  // /app/docs is the in-shell AW Docs entry that AIModeOverlay pushes when
  // the user clicks the AW Docs quick-nav button. /app/ask is the Ask AI · Code
  // surface (read-only code intelligence + PR impact analyzer). /app/knowledge is
  // the Knowledge Base surface (code chat split off Ask AI).
  if (/^\/app\/(team|missions(?:\/.*)?|radar|docs|metrics|ask|knowledge)$/.test(cleanPath)) return true
  if (
    cleanPath === '/too/team' ||
    cleanPath === '/too/teams' ||
    cleanPath === '/too/org' ||
    cleanPath === '/too/radar' ||
    cleanPath === '/too/metrics'
  ) {
    return true
  }
  if (/^\/too\/[^/]+\/(team|missions(?:\/.*)?|radar|metrics)$/.test(cleanPath)) return true
  return false
}

export function isAllowedInLaunch(
  path: string,
  config: LaunchConfig = readLaunchConfig(),
): boolean {
  if (isFullLaunch(config)) return true
  if (!isInternalPath(path)) return false
  if (isAwDocsLaunch(config)) return isAwDocsPath(path, config)
  if (isAwMissionsLaunch(config)) return isAwMissionsPath(path, config)
  return false
}

export function sanitizeLaunchReturnTo(
  raw: string,
  config: LaunchConfig = readLaunchConfig(),
): string {
  const candidate = raw.trim()
  const fallback = isAwMissionsLaunch(config)
    ? DEFAULT_TOOL_SELECTOR_PATH
    : config.defaultAwDocsPath
  if (!candidate || !isInternalPath(candidate)) return fallback
  return isAllowedInLaunch(candidate, config) ? candidate : fallback
}
